import React from 'react';
import * as types from '../api/types';

/**
 * Auto-invoicing controls on the job page (tracker item 37): the
 * "Auto-invoiced" pill on a job whose current invoice was created by
 * auto-invoicing, and the office's "Hold auto-invoicing" toggle.
 */

export const AUTO_INVOICED_LABEL = 'Auto-invoiced';
export const AUTO_INVOICE_HOLD_HINT =
  'A held job is never invoiced automatically (for example while its price is being changed). '
  + 'Cancelling an auto-created invoice puts its jobs on hold so they are not simply invoiced again.';

/** Pill on a job invoiced by auto-invoicing. */
export function AutoInvoicedBadge() {
  return (
    <span
      className="inline-flex items-center rounded-full bg-teal-50 px-2 py-0.5 text-xs font-semibold text-teal-800 ring-1 ring-teal-200"
      title="This job's invoice was created by auto-invoicing"
    >
      {AUTO_INVOICED_LABEL}
    </span>
  );
}

/**
 * Whether the hold toggle applies: office roles, and a job auto-invoicing
 * could pick up (not cancelled, not covered by a flat contract).
 */
export function showAutoInvoiceHold(canManage: boolean, job: Pick<types.Job, 'status' | 'coveredByFlatContract'>): boolean {
  return canManage && job.status !== types.JobStatus.CANCELLED && !job.coveredByFlatContract;
}

interface HoldToggleProps {
  held: boolean;
  disabled: boolean;
  onChange: (held: boolean) => void;
}

/** "Hold auto-invoicing" checkbox; the caller saves the change (PATCH /jobs/:id { autoInvoiceHold }). */
export function AutoInvoiceHoldToggle({ held, disabled, onChange }: HoldToggleProps) {
  return (
    <label className="flex items-start gap-2 text-sm">
      <input
        type="checkbox"
        className="mt-0.5 h-5 w-5 shrink-0 rounded border-gray-300 sm:mt-1 sm:h-4 sm:w-4"
        checked={held}
        disabled={disabled}
        onChange={(event) => onChange(event.target.checked)}
        aria-describedby="auto-invoice-hold-hint"
      />
      <span>
        <span className="font-semibold">Hold auto-invoicing</span>
        <span id="auto-invoice-hold-hint" className="block text-xs text-gray-500">{AUTO_INVOICE_HOLD_HINT}</span>
      </span>
    </label>
  );
}
